import { useMemo } from "react";
import { LoadingSkeleton } from "@narmi/design_system";
import { $staffApi } from "../../api";
import { PRIMACY_TIERS, TIER_LABELS } from "../primacy/constants";
import type { PrimacyTier } from "../primacy/constants";
import styles from "./PrimacyScoreCard.module.css";

export function PrimacyScoreCard() {
  const { data, isLoading } = $staffApi.useQuery("get", "/api/dashboard/primacy_statistics/");

  const overallTier = useMemo(() => {
    if (!data) return null;
    return getOverallTier(
      Number(data.average_score),
      Number(data.moderate_tier_threshold),
      Number(data.high_tier_threshold)
    );
  }, [data]);

  if (isLoading) {
    return <LoadingSkeleton isLoading />;
  }

  if (!data || !overallTier) {
    return null;
  }

  const percentages: Record<PrimacyTier, string> = {
    low: data.low_tier_percentage,
    moderate: data.moderate_tier_percentage,
    high: data.high_tier_percentage,
  };

  return (
    <div className={styles.Card}>
      <div className={styles.Title}>Aggregated primacy score</div>
      <div className={`${styles.TierLabel} margin--top--xs`} data-tier={overallTier}>
        {TIER_LABELS[overallTier]}
      </div>
      <div className={styles.BarRow}>
        {PRIMACY_TIERS.filter((tier) => Math.round(Number(percentages[tier])) > 0).map((tier) => (
          <div
            key={tier}
            className={styles.BarSegment}
            data-tier={tier}
            data-active={tier === overallTier || undefined}
            style={{ flex: Number(percentages[tier]) || 1 }}
          >
            <div className={styles.Bar} />
            <div className={styles.BarLabel}>{Number(percentages[tier]).toFixed(0)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function getOverallTier(
  averageScore: number,
  moderateThreshold: number,
  highThreshold: number
): PrimacyTier {
  if (averageScore >= highThreshold) return "high";
  if (averageScore >= moderateThreshold) return "moderate";
  return "low";
}
